// BOORO: dashboard contracts. The Start area shows what the team delivered in
// a period; a share link carries the numbers, never the tasks behind them.
import { oc } from "@orpc/contract";
import * as z from "zod";
import { TaskModelRefSchema, TaskThreadEntrySchema } from "./booro-tasks.js";
import { Id } from "./ids.js";

export const DASHBOARD_PERIODS = ["7d", "30d", "90d", "month"] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];

export const DASHBOARD_BUCKETS = ["day", "week"] as const;
export type DashboardBucket = (typeof DASHBOARD_BUCKETS)[number];

/** The numbers of one agent in a period. */
export const DashboardAgentRowSchema = z.object({
  botId: Id,
  botName: z.string(),
  botColor: z.string(),
  done: z.number().int().nonnegative(),
  successRate: z.number().nullable(),
  minutesSaved: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
});
export type DashboardAgentRow = z.infer<typeof DashboardAgentRowSchema>;

/** The numbers of one model in a period. */
export const DashboardModelRowSchema = z.object({
  ...TaskModelRefSchema.shape,
  done: z.number().int().nonnegative(),
  successRate: z.number().nullable(),
  minutesSaved: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
});
export type DashboardModelRow = z.infer<typeof DashboardModelRowSchema>;

/** One point of the timeline. */
export const DashboardPointSchema = z.object({
  /** The first day of the bucket. */
  start: z.string(),
  done: z.number().int().nonnegative(),
  minutesSaved: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
});
export type DashboardPoint = z.infer<typeof DashboardPointSchema>;

export const DashboardStatsSchema = z.object({
  period: z.enum(DASHBOARD_PERIODS),
  from: z.string(),
  to: z.string(),
  done: z.number().int().nonnegative(),
  successRate: z.number().nullable(),
  minutesSaved: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
  /** The same numbers for the period before, for the comparison. */
  previous: z.object({
    done: z.number().int().nonnegative(),
    successRate: z.number().nullable(),
    minutesSaved: z.number().int().nonnegative(),
    costUsd: z.number().nullable(),
  }),
  agents: z.array(DashboardAgentRowSchema),
  models: z.array(DashboardModelRowSchema),
  series: z.array(DashboardPointSchema),
});
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

/** A shared dashboard: the numbers of a period and nothing else. */
export const DashboardShareSchema = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
  stats: z.object({
    done: z.number().int().nonnegative(),
    successRate: z.number().nullable(),
    minutesSaved: z.number().int().nonnegative(),
    costUsd: z.number().nullable(),
  }),
  agents: z.array(DashboardAgentRowSchema),
  models: z.array(DashboardModelRowSchema),
});
export type DashboardShare = z.infer<typeof DashboardShareSchema>;

export const DashboardStatsInput = z.object({
  period: z.enum(DASHBOARD_PERIODS),
  bucket: z.enum(DASHBOARD_BUCKETS),
  /** The offices to count; every office of the account when it is left out. */
  spaceIds: z.array(Id).optional(),
  /** The agents to count; every agent when it is left out. */
  botIds: z.array(Id).optional(),
});
export type DashboardStatsInput = z.infer<typeof DashboardStatsInput>;

export const DashboardShareInput = z.object({
  period: z.enum(DASHBOARD_PERIODS),
  /** The numbers of a share cover the same filter the person looks at. */
  spaceIds: z.array(Id).optional(),
  botIds: z.array(Id).optional(),
});
export type DashboardShareInput = z.infer<typeof DashboardShareInput>;

export const booroDashboardContract = {
  stats: oc.input(DashboardStatsInput).output(DashboardStatsSchema),
  /** Creates a link that shows the numbers of a period. */
  share: oc.input(DashboardShareInput).output(z.object({ id: z.string(), url: z.string() })),
  shares: oc.output(
    z.array(z.object({ id: z.string(), from: z.string(), to: z.string(), url: z.string() })),
  ),
  revokeShare: oc
    .input(z.object({ shareId: z.string() }))
    .output(z.object({ ok: z.literal(true) })),
  /** The shared numbers, for the public page. */
  readShare: oc.input(z.object({ shareId: z.string() })).output(DashboardShareSchema),
};

/** The Chief: one agent per space who hands out the work. */
export const ChiefAgentSchema = z.object({
  botId: Id,
  /** The office the Chief works in; its conversation is reached through it. */
  spaceId: Id,
  name: z.string(),
  title: z.string(),
  color: z.string(),
});
export type ChiefAgent = z.infer<typeof ChiefAgentSchema>;

/** What the Chief reads to hand out work. */
export const TeamOverviewSchema = z.object({
  agents: z.array(
    z.object({
      botId: Id,
      name: z.string(),
      title: z.string(),
      description: z.string(),
      openTasks: z.number().int().nonnegative(),
      successRate: z.number().nullable(),
    }),
  ),
  inboxCount: z.number().int().nonnegative(),
  successRate: z.number().nullable(),
});
export type TeamOverview = z.infer<typeof TeamOverviewSchema>;

export const booroChiefContract = {
  /** The Chief of the space, created when it is asked for the first time. */
  ensure: oc.output(ChiefAgentSchema),
  /** The Chief of the space, without creating one. */
  find: oc.output(ChiefAgentSchema.nullable()),
  /** The tasks the Chief handed out, with the agent working on each. */
  tasks: oc.input(z.object({ botId: Id })).output(z.array(TaskThreadEntrySchema)),
  /** What the Chief reads to hand out work. */
  overview: oc.output(TeamOverviewSchema),
};

/** The ways a person can be reached. */
export const NOTIFICATION_CHANNELS = ["push", "email"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** What a notification can be about. */
export const NOTIFICATION_KINDS = ["completion", "failure", "help", "takeover"] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NotificationSettingsSchema = z.object({
  channel: z.enum(NOTIFICATION_CHANNELS),
  kinds: z.array(z.enum(NOTIFICATION_KINDS)),
  digestMinutes: z.number().int().nonnegative(),
  quietFrom: z.string().nullable().optional(),
  quietTo: z.string().nullable().optional(),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

export const SaveNotificationSettingsInput = z.object({
  channel: z.enum(NOTIFICATION_CHANNELS),
  kinds: z.array(z.enum(NOTIFICATION_KINDS)).optional(),
  digestMinutes: z.number().int().nonnegative().optional(),
  quietFrom: z.string().nullable().optional(),
  quietTo: z.string().nullable().optional(),
});
export type SaveNotificationSettingsInput = z.infer<typeof SaveNotificationSettingsInput>;

export const booroNotificationsContract = {
  settings: oc.output(z.array(NotificationSettingsSchema)),
  save: oc.input(SaveNotificationSettingsInput).output(NotificationSettingsSchema),
};

/** Where the spending stands against a budget. */
export const BUDGET_STATES = ["ok", "warn", "limit"] as const;
export type BudgetState = (typeof BUDGET_STATES)[number];

export const BudgetSchema = z.object({
  id: Id,
  /** Null when the budget covers the whole space. */
  botId: Id.nullable(),
  monthlyUsd: z.number().nonnegative(),
  warnAtRatio: z.number(),
  /** What one task may cost before the agent asks to go on; null keeps the default. */
  taskUsd: z.number().positive().nullable(),
  /** What was spent this month; null when a price is unknown. */
  spentUsd: z.number().nullable(),
  state: z.enum(BUDGET_STATES),
  /** Whether some costs of this month have no price. */
  hasUnknownCost: z.boolean(),
});
export type Budget = z.infer<typeof BudgetSchema>;

export const SaveBudgetInput = z.object({
  /** Null sets the budget for the whole space. */
  botId: Id.nullable(),
  /** Zero sets no monthly limit. */
  monthlyUsd: z.number().nonnegative(),
  warnAtRatio: z.number().min(0.1).max(1).optional(),
  /** Null keeps the default. */
  taskUsd: z.number().positive().nullable().optional(),
});
export type SaveBudgetInput = z.infer<typeof SaveBudgetInput>;

export const booroBudgetsContract = {
  list: oc.output(z.array(BudgetSchema)),
  save: oc.input(SaveBudgetInput).output(z.array(BudgetSchema)),
  remove: oc.input(z.object({ budgetId: Id })).output(z.object({ ok: z.literal(true) })),
};
